import React, { memo, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Stack,
  Icon,
  IconButton,
  Tooltip
} from 'investira.react.components';
import { dates, formats, validators } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';
import PropTypes from 'prop-types';
import services from '../../../services';
import CardState from './CardState';
import CardFooter from './CardFooter';

/**
 * Renderiza o componente CapitalSocialCard.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {string|number} props.pessoaId - Identificador da pessoa para uso standalone.
 * @param {string} props.pessoaNome - Nome usado para compor `aria-label`.
 * @param {Array} props.historico - Historico de capital social quando controlado por props.
 * @param {boolean} props.isLoading - Estado de carregamento controlado externamente.
 * @param {number} props.capitalSocial - Valor principal exibido no card.
 * @param {Object} props.actions - Acoes auxiliares do componente.
 * @param {Function} props.actions.formatNumber - Formatador do card.
 * @param {Function} props.onOpenHistory - Abre o historico do indicador.
 * @param {boolean} props.showChart - Controla exibicao do grafico.
 * @param {boolean} props.showHistoryButton - Controla exibicao do atalho de historico.
 * @param {Object} props.sx - Estilos adicionais para o wrapper.
 *
 * @returns {JSX.Element|null} Elemento renderizado pelo componente.
 */

const CapitalSocialCard = memo(props => {
  const params = useParams();
  const {
    pessoaId,
    pessoaNome,
    historico: pHistorico,
    isLoading: pIsLoading,
    capitalSocial,
    actions: pActions,
    onOpenHistory,
    showChart = true,
    showHistoryButton = true,
    sx,
    ...otherProps
  } = props;

  const xPessoaId = pessoaId || params?.pessoa_id;
  const [historicoLocal, setHistoricoLocal] = useState([]);
  const [isLoadingStandalone, setLoadingStandalone] = useState(false);

  const xHistorico = Array.isArray(pHistorico) ? pHistorico : historicoLocal;
  const xCapital = useMemo(
    () => [...xHistorico].reverse().find(pItem => !validators.isNull(pItem?.cs)) || {},
    [xHistorico]
  );
  const xIsLoading = typeof pIsLoading === 'boolean' ? pIsLoading : isLoadingStandalone;

  const xCapitalSocialChartData = useMemo(
    () =>
      xHistorico
        .filter(xPoint => !validators.isNull(xPoint?.cs))
        .map(xPoint => [dates.toDate(xPoint.data).getTime(), xPoint.cs]),
    [xHistorico]
  );

  const xCapitalSocialChartOptions = useMemo(
    () => ({
      title: false,
      chart: {
        type: 'line',
        height: 40,
        margin: [2, 4, 2, 4],
        spacing: [0, 0, 0, 0]
      },
      legend: { enabled: false },
      yAxis: {
        visible: false,
        minPadding: 0.1,
        maxPadding: 0.1
      },
      xAxis: {
        type: 'datetime',
        visible: false,
        minPadding: 0.03,
        maxPadding: 0.03
      },
      series: [
        {
          name: 'Capital social',
          type: 'line',
          data: xCapitalSocialChartData,
          marker: {
            enabled: true,
            radius: 2.5
          }
        }
      ],
      tooltip: {
        formatter: function () {
          const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
          const xValue = formats.friendlyNumber(this.point.y, 2, false);
          const xFullValue = formats.formatNumber(this.point.y, 2, true, false);

          return `<span style="font-size: 1em"><b>${xDate}</b></span><br/><span style="color:${this.color}">●</span>${this.series.name}: <b>${xValue}</b> (${xFullValue})<br/>`;
        }
      }
    }),
    [xCapitalSocialChartData]
  );

  useEffect(() => {
    if (Array.isArray(pHistorico)) {
      return;
    }

    if (!xPessoaId) {
      setHistoricoLocal([]);
      setLoadingStandalone(false);
      return;
    }

    const xToday = new Date();

    setLoadingStandalone(true);
    services.pessoas.cs(
      {
        data: null,
        data_inicio: dates.addYears(dates.toSqlDate(xToday), -10),
        data_fim: dates.toSqlDate(xToday),
        pessoa_id: xPessoaId,
        sort: 'data ASC'
      },
      rRes => {
        setHistoricoLocal(rRes?.data || []);
      },
      null,
      () => {
        setLoadingStandalone(false);
      }
    );

    return () => {
      if (services.pessoas?.cancel?.cs?.cancel) {
        services.pessoas.cancel.cs.cancel();
      }
    };
  }, [pHistorico, xPessoaId]);

  const xValue = !validators.isNull(capitalSocial) ? capitalSocial : xCapital?.cs;
  const xShowCapitalCard = !validators.isNull(xValue) || !validators.isEmpty(xCapital);

  const formatNumber = (pValue, pCurrency = false, pDecimals = 0) => {
    if (validators.isNull(pValue)) {
      return '-';
    }

    if (pActions?.formatNumber) {
      return pActions.formatNumber(pValue, pCurrency, pDecimals);
    }

    if (pCurrency) {
      return formats.friendlyNumber(pValue, pDecimals || 2, false);
    }

    return formats.formatNumber(pValue, pDecimals, false, false);
  };

  const handleOpenClick = () => {
    onOpenHistory && onOpenHistory('cs');
  };

  if (!xIsLoading && !xShowCapitalCard) {
    return null;
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        ...sx
      }}
      {...otherProps}>
      {xShowCapitalCard || xIsLoading ? (
        <Card
          sx={{ minHeight: '100%' }}
          aria-label={pessoaNome ? `Capital social: ${pessoaNome}` : 'Capital social'}>
          <CardContent sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1}>
                <Typography variant="caption" color="textPrimary" gutterBottom component="h4">
                  Capital social
                </Typography>
              </Stack>
              {xShowCapitalCard && showHistoryButton && (
                <Stack>
                  <Tooltip title="Histórico Capital Social">
                    <div>
                      <IconButton color="primary" onClick={handleOpenClick}>
                        <Icon iconName="chart_bar" size={16} />
                      </IconButton>
                    </div>
                  </Tooltip>
                </Stack>
              )}
            </Stack>

            <CardState
              loading={xIsLoading}
              data={!validators.isNull(xValue) ? { cs: xValue } : xCapital}>
              <Stack spacing={1} justifyContent="space-between" sx={{ minHeight: '100%' }}>
                <Stack spacing={1}>
                  <Stack>
                    <Typography variant="h4" color="textPrimary" sx={{ fontWeight: 600 }}>
                      {formatNumber(xValue, true, 2)}
                    </Typography>

                    {!validators.isNull(xCapital?.data) && (
                      <Typography variant="caption" color="textPrimary" gutterBottom>
                        Data de referência {formats.formatDateCustom(xCapital?.data, 'DD/MMM/YYYY')}
                      </Typography>
                    )}
                  </Stack>

                  {showChart && !validators.isEmpty(xCapitalSocialChartData) && (
                    <Stack minWidth={0} sx={{ width: '100%' }}>
                      <HighchartsReact options={xCapitalSocialChartOptions} />
                    </Stack>
                  )}
                </Stack>

                <CardFooter
                  consolidacao={xCapital?.consolidacao_tipo}
                  atualizado={xCapital?.data}
                  origem={xCapital?.origem}
                  pc={xCapital?.pc}
                />
              </Stack>
            </CardState>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
});

CapitalSocialCard.displayName = 'CapitalSocialCard';

CapitalSocialCard.propTypes = {
  pessoaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pessoaNome: PropTypes.string,
  historico: PropTypes.array,
  isLoading: PropTypes.bool,
  capitalSocial: PropTypes.number,
  actions: PropTypes.shape({
    formatNumber: PropTypes.func
  }),
  onOpenHistory: PropTypes.func,
  showChart: PropTypes.bool,
  showHistoryButton: PropTypes.bool,
  sx: PropTypes.object
};

export default CapitalSocialCard;
